import { useState, useEffect } from "react";
import { format, addDays, startOfToday, isToday } from "date-fns";
import { es } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { getDisponibilidad } from "@/lib/api";

function DiaRow({ fecha, slots, showNombre, onSelectHueco }) {
  const label = format(fecha, "EEEE d MMM", { locale: es });
  const fechaStr = format(fecha, "yyyy-MM-dd");
  const count = slots.length;
  return (
    <div className="p-3 rounded-lg border">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium capitalize">{label}</span>
        <Badge variant={count > 0 ? "secondary" : "outline"} className="text-xs">
          {count > 0 ? count + " huecos" : "Sin huecos"}
        </Badge>
      </div>
      {count > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {slots.map((s, i) => (
            <button
              key={`${s.hora}-${s.peluquero_id}-${i}`}
              type="button"
              data-testid={`hueco-${fechaStr}-${s.hora}-${s.peluquero_id}`}
              onClick={() => onSelectHueco({
                fecha: fechaStr,
                hora: s.hora,
                peluquero_id: s.peluquero_id,
                peluquero_nombre: s.peluquero_nombre,
                peluquero_color: s.peluquero_color,
              })}
              className="flex items-center gap-1.5 text-xs px-2 py-1 rounded border hover:border-accent hover:bg-accent/5 transition-colors"
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.peluquero_color || "#3A5A40" }} />
              <span className="font-mono font-medium">{s.hora}</span>
              {showNombre && <span className="text-muted-foreground">— {s.peluquero_nombre}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DisponibilidadSemanal({ servicioId, peluqueros = [], onSelectHueco }) {
  const [dias, setDias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPelId, setSelectedPelId] = useState(null); // null = Cualquiera

  useEffect(() => {
    if (!servicioId) return;
    let cancelled = false;
    async function fetchDias() {
      setLoading(true);
      try {
        const results = [];
        const today = startOfToday();
        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();
        for (let i = 0; i < 7; i++) {
          const fecha = addDays(today, i);
          const fechaStr = format(fecha, "yyyy-MM-dd");
          const res = await getDisponibilidad(servicioId, fechaStr, selectedPelId || undefined);
          let slots = res.data.slots || [];
          if (isToday(fecha)) {
            slots = slots.filter((s) => {
              const [h, m] = s.hora.split(":").map(Number);
              return h * 60 + m > nowMin;
            });
          }
          slots.sort((a, b) => a.hora.localeCompare(b.hora) || a.peluquero_nombre.localeCompare(b.peluquero_nombre));
          results.push({ fecha, fechaStr, slots });
        }
        if (!cancelled) setDias(results);
      } catch (e) {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchDias();
    return function () { cancelled = true; };
  }, [servicioId, selectedPelId]);

  const showNombre = !selectedPelId; // "Cualquiera" -> show which barber is free

  return (
    <div className="space-y-3">
      <div className="mb-2">
        <Label className="text-base mb-3 block">Filtrar por peluquero</Label>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedPelId === null ? "default" : "outline"}
            className="h-10"
            onClick={() => setSelectedPelId(null)}
            data-testid="huecos-filtro-cualquiera"
          >
            Cualquiera
          </Button>
          {peluqueros.map((p) => (
            <Button
              key={p.id}
              variant={selectedPelId === p.id ? "default" : "outline"}
              className="h-10"
              onClick={() => setSelectedPelId(p.id)}
              data-testid={`huecos-filtro-${p.id}`}
            >
              <span className="w-2.5 h-2.5 rounded-full mr-1.5" style={{ backgroundColor: p.color }} />
              {p.nombre}
            </Button>
          ))}
        </div>
      </div>

      <p className="text-muted-foreground text-sm">Huecos disponibles en los proximos 7 dias</p>

      {loading ? (
        <div className="flex justify-center py-8"><div className="spinner w-8 h-8" /></div>
      ) : (
        dias.map((dia) => (
          <DiaRow
            key={dia.fechaStr}
            fecha={dia.fecha}
            slots={dia.slots}
            showNombre={showNombre}
            onSelectHueco={onSelectHueco}
          />
        ))
      )}
    </div>
  );
}
